import React from 'react';
import styled from 'styled-components';
import { UnifreightValidationError } from '../../../models/entities/shipment/unifreightIntegration';
import theme from '../Theme/theme';

interface Props {
    visible: boolean;
    errors: UnifreightValidationError[];
    errorCount: number;
    message: string;
    onClose: () => void;
    localization: any;
}

interface GroupedErrors {
    partners: UnifreightValidationError[];
    locations: UnifreightValidationError[];
    shipmentData: UnifreightValidationError[];
    tradeTerms: UnifreightValidationError[];
}

const UnifreightValidationErrorModal: React.FC<Props> = ({ visible, errors, errorCount, message, onClose, localization }) => {
    if (!visible) return null;

    // Group errors by category
    const groupedErrors: GroupedErrors = {
        partners: [],
        locations: [],
        shipmentData: [],
        tradeTerms: []
    };

    errors.forEach((error) => {
        groupedErrors[error.category].push(error);
    });

    const getCategoryTitle = (category: string): string => {
        const categoryMap: { [key: string]: string } = {
            partners: localization.unifreight_error_category_partners || 'Partners',
            locations: localization.unifreight_error_category_locations || 'Locations',
            shipmentData: localization.unifreight_error_category_shipment_data || 'Shipment Data',
            tradeTerms: localization.unifreight_error_category_trade_terms || 'Trade Terms'
        };
        return categoryMap[category] || category;
    };

    return (
        <Overlay onClick={onClose}>
            <ModalContainer onClick={(e) => e.stopPropagation()}>
                <ModalHeader>
                    <HeaderTitle>
                        <ErrorIcon className="material-icons">error_outline</ErrorIcon>
                        {localization.unifreight_validation_errors || 'Validation Errors'}
                    </HeaderTitle>
                    <CloseButton onClick={onClose}>
                        <span className="material-icons">close</span>
                    </CloseButton>
                </ModalHeader>

                <ModalContent>
                    <ErrorSummary>
                        {message || `Please fix ${errorCount} error${errorCount !== 1 ? 's' : ''} before sending to Unifreight.`}
                    </ErrorSummary>

                    {Object.entries(groupedErrors).map(([category, categoryErrors]) => {
                        if (categoryErrors.length === 0) return null;

                        return (
                            <ErrorCategory key={category}>
                                <CategoryTitle>{getCategoryTitle(category)}</CategoryTitle>
                                <ErrorList>
                                    {categoryErrors.map((error, index) => (
                                        <ErrorItem key={`${error.field}-${index}`}>
                                            <ErrorBullet className="material-icons">fiber_manual_record</ErrorBullet>
                                            <ErrorMessage>{error.message}</ErrorMessage>
                                        </ErrorItem>
                                    ))}
                                </ErrorList>
                            </ErrorCategory>
                        );
                    })}
                </ModalContent>

                <ModalFooter>
                    <CloseButtonStyled onClick={onClose}>{localization.close || 'Close'}</CloseButtonStyled>
                </ModalFooter>
            </ModalContainer>
        </Overlay>
    );
};

const Overlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.75);
    z-index: 1201;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const ModalContainer = styled.div`
    background: white;
    border-radius: 12px;
    box-shadow: 0 0 18px 15px rgba(0, 0, 0, 0.28);
    width: 90%;
    max-width: 700px;
    max-height: 80vh;
    display: flex;
    flex-direction: column;
    animation: slideUp 0.3s ease-out;

    @keyframes slideUp {
        from {
            opacity: 0;
            transform: translateY(20px);
        }
        to {
            opacity: 1;
            transform: translateY(0);
        }
    }
`;

const ModalHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 20px 30px;
    border-bottom: 1px solid #e0e0e0;
`;

const HeaderTitle = styled.div`
    display: flex;
    align-items: center;
    font-size: 20px;
    font-weight: bold;
    color: ${theme.colors.pageTitle};
`;

const ErrorIcon = styled.span`
    color: #f44336;
    margin-right: 10px;
    font-size: 28px !important;
`;

const CloseButton = styled.div`
    cursor: pointer;
    color: #666;
    display: flex;
    align-items: center;

    &:hover {
        color: #333;
    }

    span {
        font-size: 24px;
    }
`;

const ModalContent = styled.div`
    padding: 20px 30px;
    overflow-y: auto;
    flex: 1;
`;

const ErrorSummary = styled.div`
    background-color: #fff3e0;
    border-left: 4px solid #ff9800;
    padding: 15px;
    margin-bottom: 20px;
    border-radius: 4px;
    color: #e65100;
    font-weight: 500;
`;

const ErrorCategory = styled.div`
    margin-bottom: 25px;

    &:last-child {
        margin-bottom: 0;
    }
`;

const CategoryTitle = styled.div`
    font-size: 16px;
    font-weight: 600;
    color: ${theme.colors.pageTitle};
    margin-bottom: 10px;
    padding-bottom: 5px;
    border-bottom: 2px solid #e0e0e0;
`;

const ErrorList = styled.ul`
    list-style: none;
    padding: 0;
    margin: 0;
`;

const ErrorItem = styled.li`
    display: flex;
    align-items: flex-start;
    padding: 8px 0;
`;

const ErrorBullet = styled.span`
    font-size: 8px !important;
    color: #f44336;
    margin-right: 10px;
    margin-top: 6px;
`;

const ErrorMessage = styled.span`
    color: #424242;
    line-height: 1.5;
`;

const ModalFooter = styled.div`
    display: flex;
    justify-content: flex-end;
    padding: 15px 30px;
    border-top: 1px solid #e0e0e0;
`;

const CloseButtonStyled = styled.button`
    background-color: #2196f3;
    color: white;
    border: none;
    padding: 10px 24px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px;
    font-weight: 500;
    transition: background-color 0.2s;

    &:hover {
        background-color: #1976d2;
    }

    &:active {
        background-color: #0d47a1;
    }
`;

export default UnifreightValidationErrorModal;
